// Auth context. Uses AWS Cognito when the pool + client id are configured at
// build time (production); otherwise falls back to a localStorage demo session
// so local dev works without a backend.
import { createContext, useContext, useState } from 'react'
import {
  isCognitoConfigured,
  signIn,
  signOut,
  updateName as cognitoUpdateName,
  nameFromIdToken,
  displayName,
  NAME_MAX,
} from './lib/cognito.js'
import { logEvent, flushEventOutbox } from './lib/events.js'
import { setUserScope } from './lib/userScope.js'
import { setActingAs } from './lib/chatCloud.js'
import { forgetForeignScope } from './lib/chat.js'

const AuthContext = createContext(null)
const STORAGE_KEY = 'revailosales.session'

// The old demo identity is retired outright: a browser still carrying it is
// signed out (no silent swap to another account — sessions are only ever
// created by an actual login).
const RETIRED_EMAIL = 'demo@revailosales.com'

// First *character*, not first UTF-16 code unit: `'𝓑eta'[0]` is half a surrogate
// pair and renders as a replacement glyph, and names are user-supplied now.
function firstLetter(str) {
  return Array.from(String(str || '').trim())[0] || ''
}

export function initialsOf(name = '') {
  const parts = name.trim().split(/\s+/)
  const letters = firstLetter(parts[0]) + firstLetter(parts[1])
  return letters.toUpperCase() || 'U'
}

// Avatar initials for the signed-in user: first + last initial once they've set
// a name (so "Yehuda Tsadok" is YT even if only one part is filled in), falling
// back to the old derivation from the display name.
export function initialsOfUser(user) {
  const first = String(user?.firstName || '').trim()
  const last = String(user?.lastName || '').trim()
  if (first || last) return (firstLetter(first) + firstLetter(last)).toUpperCase()
  return initialsOf(user?.name || '')
}

function demoSession(email) {
  return {
    email: email.trim(),
    firstName: '',
    lastName: '',
    // Same derivation as the Cognito path, so a no-name account reads the same
    // in both modes instead of "Jane Doe" here and "jane.doe" there.
    name: displayName({ email }),
    role: 'Administrator',
  }
}

// Sessions stored before first/last names were tracked separately carry only
// `name`. Recover the parts from the ID token already in the session (offline)
// so Settings doesn't show empty fields to someone who does have a name set.
function hydrate(session) {
  if (!session || typeof session !== 'object') return session
  if (session.firstName !== undefined || session.lastName !== undefined) return session
  const parts = session.idToken ? nameFromIdToken(session.idToken) : { firstName: '', lastName: '' }
  return {
    ...session,
    ...parts,
    name:
      parts.firstName || parts.lastName
        ? displayName({ email: session.email, ...parts })
        : session.name,
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY)
      let parsed = raw ? JSON.parse(raw) : null
      // Retire the old demo account: sign it out instead of inventing a session.
      if (parsed?.email?.trim().toLowerCase() === RETIRED_EMAIL) {
        parsed = null
        localStorage.removeItem(STORAGE_KEY)
      }
      // Point the per-user overlay stores at this account before anything reads them.
      setUserScope(parsed?.email || null)
      return hydrate(parsed)
    } catch {
      return null
    }
  })

  // Impersonation ("Sign in as") — the workspace owner/admin can view the app
  // as any roster member. It overrides the *effective* user so RBAC (resolved
  // from user.email against the roster) and the per-user overlay scope follow
  // the target, while every API call still carries the REAL owner's Cognito
  // token (getIdToken reads the Cognito session, not this state) — the backend
  // never sees a forged identity. ONE backend does honour who is being viewed
  // as: chat history, which keys threads on the token's Cognito sub and so
  // used to answer with the owner's own threads while the client cached them
  // under the member's scope ("his chat" was the admin's). For that endpoint
  // only, chatCloud.js sends an `X-Act-As: <member>` header the server checks
  // against the roster (owner/admin caller, target ranked strictly below) and
  // audits on every write; a refusal is shown as a refusal, never as the
  // owner's rows. Held in memory only — a refresh returns to the real account.
  // Mirrors the pattern in the sibling dashboard.
  const [impersonated, setImpersonated] = useState(null)

  // Persist + publish a session without re-announcing a sign-in.
  function persist(session) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(session))
    setUser(session)
    return session
  }

  // A roster member ({ email, name, role, permissions? }) as an effective user.
  function memberToUser(member) {
    const name = String(member.name || '').trim() || displayName({ email: member.email })
    const parts = name.split(/\s+/)
    return {
      email: String(member.email || '').trim().toLowerCase(),
      firstName: parts[0] || '',
      lastName: parts.slice(1).join(' ') || '',
      name,
      role: member.role || 'rep',
      ...(Array.isArray(member.permissions) ? { permissions: member.permissions } : {}),
    }
  }

  function impersonate(member) {
    if (!member?.email) return
    // Acting target BEFORE the scope, and in the same tick, so no request can
    // be pinned between the two. chat.js pins the pair and voids an operation
    // that sees the owner's scope with the member as its target — its answer
    // would be the member's rows cached under the owner's key.
    setActingAs(member.email)
    // FORGET THIS BROWSER'S COPY OF THE MEMBER'S CHAT before pointing the
    // scope at it. A member's scope in the owner's browser is a view of their
    // cloud history, never its home — and before the act-as header existed
    // the sync ran bare and filled that scope with the OWNER's threads under
    // the member's name. Left in place, that copy paints as the member's chat
    // (and, once the header works, would be pushed INTO the member's account
    // on the first drain). Dropped here, so the first act-as read is the only
    // thing that fills it. Never touches the owner's own scope.
    forgetForeignScope(member.email)
    // Point per-user overlays at the target so the view is faithfully theirs.
    setUserScope(member.email)
    setImpersonated(member)
    // The ONE place this is logged (Collaboration's button used to log it too,
    // which would have written two rows per click). Actor is the owner's token
    // server-side; `target` names who was being viewed as.
    logEvent('impersonate_start', {
      actor: user?.email,
      actorName: user?.name,
      detail: `Viewing the workspace as ${member.email}`,
      data: { target: String(member.email || '').trim().toLowerCase() },
    })
  }

  function stopImpersonating() {
    setImpersonated(null)
    // Cleared BEFORE the scope returns to the owner: a request pinned in
    // between must not carry the member's header against the owner's scope.
    setActingAs(null)
    setUserScope(user?.email || null)
    logEvent('impersonate_stop', {
      actor: user?.email,
      actorName: user?.name,
      detail: 'Returned to their own account',
    })
  }

  function setSession(session) {
    setUserScope(session.email)
    persist(session)
    logEvent('sign_in', {
      actor: session.email,
      actorName: session.name,
      detail: `Signed in to the workspace`,
    })
    // A fresh sign-in is the moment a lapsed session's queued events (the
    // outbox in events.js) can finally be uploaded. Fire-and-forget.
    try {
      flushEventOutbox()
    } catch {
      /* never block sign-in on the ledger */
    }
    return session
  }

  // Returns { status: 'OK' } once signed in, or { status: 'NEW_PASSWORD_REQUIRED',
  // setPassword } for invited users who must set a password on first login.
  async function login(email, password) {
    if (isCognitoConfigured) {
      const result = await signIn(email, password)
      if (result.status === 'OK') {
        setSession(result.user)
        return { status: 'OK' }
      }
      // Wrap the challenge so the caller can complete it and get a session.
      return {
        status: 'NEW_PASSWORD_REQUIRED',
        setPassword: async (newPassword) => setSession(await result.setPassword(newPassword)),
      }
    }
    // Demo fallback: accept anything.
    setSession(demoSession(email))
    return { status: 'OK' }
  }

  // Save the signed-in user's first/last name. In Cognito mode this writes the
  // standard given_name / family_name attributes on the account — it deliberately
  // does NOT fall back to localStorage when the pool rejects the write, so a
  // failure surfaces instead of leaving a name that only exists in this browser.
  // Whitespace-only counts as unset. Throws with a displayable message.
  async function updateName(firstName, lastName) {
    if (!user?.email) throw new Error('You are not signed in.')
    const first = String(firstName || '').trim()
    const last = String(lastName || '').trim()
    if (first.length > NAME_MAX || last.length > NAME_MAX)
      throw new Error(`Each name has to be ${NAME_MAX} characters or fewer.`)

    if (isCognitoConfigured) {
      const profile = await cognitoUpdateName(user.email, { firstName: first, lastName: last })
      return persist({ ...user, ...profile })
    }
    // Demo session — there is no pool to write to, so this browser is the only
    // store there is (Settings says as much next to the field).
    return persist({
      ...user,
      firstName: first,
      lastName: last,
      name: displayName({ email: user.email, firstName: first, lastName: last }),
    })
  }

  function logout() {
    if (isCognitoConfigured) {
      try {
        signOut()
      } catch {
        /* ignore */
      }
    }
    setImpersonated(null)
    localStorage.removeItem(STORAGE_KEY)
    // The RBAC cache is per-user; leaving it behind would hand the next
    // sign-in this user's grants for the first paint.
    localStorage.removeItem('revailosales.rbac')
    // An impersonation must not outlive the session that was allowed it: the
    // next sign-in would otherwise send its first chat request as the member.
    setActingAs(null)
    setUserScope(null)
    setUser(null)
  }

  // What the app renders as "the current user": the impersonated member when
  // active, otherwise the real session. `realUser` stays the true account so a
  // banner (and the owner-only impersonation gate) can reference it.
  const effectiveUser = impersonated && user ? { ...memberToUser(impersonated), impersonated: true } : user

  return (
    <AuthContext.Provider
      value={{
        user: effectiveUser,
        realUser: user,
        isImpersonating: !!impersonated,
        impersonate,
        stopImpersonating,
        login,
        logout,
        setSession,
        updateName,
        isCognito: isCognitoConfigured,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider')
  return ctx
}
